import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { count, formatDay, idCheckLabel, idCheckTone, listPeople, listRowLimit, loadAllRows, orDash, personTypeLabel, personTypes, signedUpAsLabel } from './adminData.js';
import { formatWhen } from './staffAdmin.js';
import PersonRecord from './PersonRecord.jsx';
import { useSort } from './SortableTable.jsx';
import { labelOf } from './sorting.js';

const typeOptions = [['all', 'All'], ...personTypes];
const faint = { color: 'var(--muted)', fontStyle: 'italic' };
const noRows = [];
const sortBy = {
  name: { value: (row) => row.name },
  email: { value: (row) => row.email },
  type: { value: (row) => (Array.isArray(row.types) ? row.types.map(personTypeLabel) : null) },
  signedUpAs: { value: (row) => labelOf(row.signed_up_as, signedUpAsLabel) },
  joined: { value: (row) => row.joined_at, first: 'desc' },
  lastSignedIn: { value: (row) => row.last_sign_in_at, first: 'desc' },
  idCheck: { value: (row) => labelOf(row.id_check, idCheckLabel) },
};

/** Everyone with an account, by type, with a search. Opening a name shows everything we hold on them. */
export default function PeopleTab({ type = 'all', personId, onOpen }) {
  // A link with a type we don't know would be refused by the database, so treat it as "All".
  const activeType = typeOptions.some(([key]) => key === type) ? type : 'all';
  if (personId) return <PersonRecord key={personId} id={personId} backType={activeType} onOpen={onOpen}/>;
  return <PeopleList type={activeType} onOpen={onOpen}/>;
}

function PeopleList({ type, onOpen }) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ rows: null, total: 0, capped: false, error: '' });
  // Every request takes a number; only the newest one is allowed to change the screen.
  const latest = useRef(0);
  const { rows, header } = useSort(state.rows || noRows, sortBy);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  // A new type or search reads the whole list again, 200 people at a time.
  useEffect(() => {
    const mine = ++latest.current;
    setState({ rows: null, total: 0, capped: false, error: '' });
    loadAllRows((page) => listPeople(supabase, { type, query, ...page }), { isCurrent: () => latest.current === mine })
      .then((all) => {
        if (all && latest.current === mine) setState({ rows: all.rows, total: all.total, capped: all.capped, error: '' });
      })
      .catch((cause) => {
        if (latest.current === mine) setState({ rows: [], total: 0, capped: false, error: cause.message });
      });
    return () => { latest.current += 1; };
  }, [type, query]);

  const { total, capped, error } = state;

  return (
    <>
      <div className="mod-filters">
        <label>Type
          <select value={type} onChange={(event) => onOpen({ tab: 'people', type: event.target.value })}>
            {typeOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label><span className="visually-hidden">Search</span>
          <input type="search" value={input} onChange={(event) => setInput(event.target.value)} placeholder="Name or email"/>
        </label>
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {state.rows === null
        ? <p>Loading people…</p>
        : (
          <>
            {rows.length === 0
              ? (!error && <p className="admin-empty">No people match.</p>)
              : (
                <div className="mod-table-wrap">
                  <table className="mod-audit">
                    <thead>
                      <tr>
                        {header('name', 'Name')}{header('email', 'Email')}{header('type', 'Type')}{header('signedUpAs', 'Signed up as')}
                        {header('joined', 'Joined')}{header('lastSignedIn', 'Last signed in')}{header('idCheck', 'ID check')}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <button type="button" className="admin-link" onClick={() => onOpen({ tab: 'people', type, person: row.id })}>
                              {row.name || <span style={faint}>No name given</span>}
                            </button>
                            {row.suspended && <> <span className="admin-status is-bad">Suspended</span></>}
                          </td>
                          <td>{orDash(row.email)}</td>
                          <td>
                            {Array.isArray(row.types) && row.types.length > 0
                              ? (
                                <span className="admin-badges">
                                  {row.types.map((key) => <span key={key} className={`admin-badge${key === 'staff' ? ' is-staff' : ''}`}>{personTypeLabel(key)}</span>)}
                                </span>
                              )
                              : '–'}
                          </td>
                          <td>{signedUpAsLabel(row.signed_up_as)}</td>
                          <td>{formatDay(row.joined_at)}</td>
                          <td>{row.last_sign_in_at ? formatWhen(String(row.last_sign_in_at)) : 'Never'}</td>
                          <td><span className={`admin-status is-${idCheckTone(row.id_check)}`}>{idCheckLabel(row.id_check)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            {capped && <p className="mod-note">{`Showing the first ${count(listRowLimit)}.`}</p>}
          </>
        )}
    </>
  );
}
